import { useCallback, useEffect, useRef, useState } from "react";
import { Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import * as styles from "../styles_vanilla.css";

const CARBONE_SCRIPT_ID = "carbone-studio-script";
const CARBONE_SCRIPT_SRC = "/services/carbone/carbone-studio.js";

/**
 * Singleton promise for loading the Carbone Studio script.
 * We use a module-level variable because:
 * 1. The script only needs to be loaded once per page lifecycle
 * 2. Multiple component instances can share the same loaded script
 * 3. Custom elements (once defined) cannot be undefined, so unloading serves no purpose
 */
let scriptLoadPromise: Promise<void> | null = null;

const loadCarboneScript = (): Promise<void> => {
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = CARBONE_SCRIPT_ID;
    script.src = CARBONE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load Carbone Studio script"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "carbone-studio": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        ref?: React.Ref<CarboneStudioElement>;
      };
    }
  }
}

type CarboneStudioElement = HTMLElement & {
  setConfig: (config: CarboneStudioConfig) => void;
  openTemplate: (templateFile: TemplateFile, options?: StudioOptions) => void;
  addEventListener: (event: string, handler: (e: CustomEvent<unknown>) => void) => void;
  removeEventListener: (event: string, handler: (e: CustomEvent<unknown>) => void) => void;
};

type CarboneStudioConfig = {
  origin?: string;
  token?: string;
  fetchOverride?: (url: string, options: RequestInit) => Promise<Response>;
  theme?: string;
};

type TemplateFile = {
  templateId: string;
  extension: string;
};

type StudioOptions = {
  data?: Record<string, unknown>;
  complement?: Record<string, unknown>;
  enum?: Record<string, unknown>;
  translations?: Record<string, unknown>;
  lang?: string;
  timezone?: string;
  currencySource?: string;
  currencyTarget?: string | null;
};

type TemplateUpdateDetail = {
  dataURI: string;
  type: string;
  createdAt: number;
};

export type TemplateFileData = {
  dataURI: string;
  fileType: string;
};

export type TemplateCreationProps = {
  onTemplateChange?: (data: TemplateFileData) => void;
  isActive?: boolean;
};

export const TemplateCreation = ({
  onTemplateChange,
  isActive = true,
}: TemplateCreationProps) => {
  const studioRef = useRef<CarboneStudioElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Ref pattern to avoid stale closures in event handlers.
   * The event listener is registered once during initialization, but we need it
   * to always call the latest `onTemplateChange` callback. Without this ref,
   * the handler would capture the initial callback and never see updates.
   */
  const onTemplateChangeRef = useRef(onTemplateChange);
  useEffect(() => {
    onTemplateChangeRef.current = onTemplateChange;
  }, [onTemplateChange]);

  const handleTemplateUpdate = useCallback((e: CustomEvent<unknown>) => {
    const detail = e.detail as TemplateUpdateDetail;
    const { dataURI, type } = detail;
    if (onTemplateChangeRef.current && dataURI && type) {
      onTemplateChangeRef.current({ dataURI, fileType: type });
    }
  }, []);

  /**
   * Carbone Studio has its own base font size assumptions. We need to override
   * the root font size when the studio is active to ensure proper rendering.
   * The class is added to <html> so it takes precedence over other styles.
   */
  useEffect(() => {
    if (isActive) {
      document.documentElement.classList.add(styles.carboneStudioActive);
    } else {
      document.documentElement.classList.remove(styles.carboneStudioActive);
    }

    return () => {
      document.documentElement.classList.remove(styles.carboneStudioActive);
    };
  }, [isActive]);

  useEffect(() => {
    let isMounted = true;
    let studioElement: CarboneStudioElement | null = null;

    const initStudio = async () => {
      try {
        await loadCarboneScript();

        // Wait for the custom element to be defined with a timeout to prevent
        // hanging indefinitely if something goes wrong during script execution
        let timeoutId: ReturnType<typeof setTimeout>;
        const timeout = new Promise<void>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error("Timeout loading Carbone Studio")),
            10000,
          );
        });

        try {
          await Promise.race([customElements.whenDefined("carbone-studio"), timeout]);
        } finally {
          clearTimeout(timeoutId!);
        }

        // Guard against state updates if component unmounted during async loading
        if (!isMounted || !studioRef.current) {
          return;
        }

        studioElement = studioRef.current;

        const carboneOrigin = `${window.location.origin}/services/carbone`;

        studioElement.setConfig({
          origin: carboneOrigin,
          // Authentication/authorization is handled by the backend.
          // Carbone Studio makes internal fetch calls. We override to add the
          // X-Requested-With header for CSRF protection on our backend.
          fetchOverride: (url: string, options: RequestInit) => {
            const headers = new Headers(options.headers);
            headers.set("X-Requested-With", "XMLHttpRequest");
            return fetch(url, { ...options, headers });
          },
          theme: `main { --primary: ${Colors.Success600}; }`,
        });
        studioElement.addEventListener("template:updated", handleTemplateUpdate);

        setIsLoading(false);
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : "Failed to load Carbone Studio");
          setIsLoading(false);
        }
      }
    };

    void initStudio();

    return () => {
      isMounted = false;
      if (studioElement) {
        studioElement.removeEventListener("template:updated", handleTemplateUpdate);
      }
    };
  }, [handleTemplateUpdate]);

  return (
    <div className={styles.container}>
      {error && (
        <Flex
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          className={styles.container}
        >
          <Text variant="h3">Error Loading Carbone Studio</Text>
          <Text variant="regular-md">{error}</Text>
          <Text variant="regular-sm">
            Make sure the carbone-studio.js script is loaded correctly.
          </Text>
        </Flex>
      )}
      {isLoading && !error && (
        <Flex justifyContent="center" alignItems="center" className={styles.container}>
          <LoadingIndicator />
        </Flex>
      )}
      <carbone-studio
        ref={studioRef}
        className={isLoading || error ? styles.hidden : styles.studio}
        style={{ height: "100%", width: "100%" }}
      />
    </div>
  );
};
